import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNotifier } from "../../../../Core/Notifier";

/**
 * Staff Portal one-time credential dialog.
 *
 * Serves BOTH the create-account and reset-password flows. The dialog
 * has two phases:
 *
 *   1. FORM  — mode picker (auto / manual). Manual reveals a new/
 *              confirm password pair. Submit hands the plaintext to
 *              the parent via `onSubmit(mode, password | null)`.
 *   2. RESULT — one-time display of the temporary password returned
 *              from the create/reset response. Three copy actions +
 *              a "Save these now" security notice. Once dismissed the
 *              password is not retrievable — the DB only ever held
 *              the bcrypt hash.
 *
 * The parent component controls the phase — pass `credential` non-null
 * to jump directly to the RESULT phase after a successful mutation.
 *
 * Never displays an existing/persisted password. The one-time
 * plaintext originates from the BE response (`temporary_password`)
 * and lives only in this component's props for as long as the dialog
 * is open.
 */

export type PasswordMode = "auto" | "manual";

type Props = {
  open: boolean;
  mode: "create" | "reset";
  staffName: string;
  staffIdLabel?: string | null;
  loginEmail?: string | null;
  /**
   * True when the linked User row has a non-empty password hash — the
   * only signal the backend exposes about the current password.
   * Rendered in the identity block as "Current Password: Set".
   * NEVER a plaintext or a hash; passwords are one-way hashed and
   * cannot be shown to the admin. Optional so the create-account
   * flow (where no user exists yet) can omit it.
   */
  passwordIsSet?: boolean;
  isSubmitting?: boolean;
  /** When non-null, dialog skips the form and shows the one-time credential display. */
  credential?: { password: string } | null;
  onSubmit: (payload: { mode: PasswordMode; password: string | null }) => void;
  onClose: () => void;
};

const validateManualPassword = (pw: string): string | null => {
  if (pw.length < 8) return "Password must be at least 8 characters.";
  if (!/[A-Za-z]/.test(pw)) return "Password must contain at least one letter.";
  if (!/\d/.test(pw)) return "Password must contain at least one number.";
  return null;
};

const StaffPortalCredentialDialog: React.FC<Props> = ({
  open,
  mode,
  staffName,
  staffIdLabel,
  loginEmail,
  passwordIsSet,
  isSubmitting,
  credential,
  onSubmit,
  onClose,
}) => {
  const { showMessage } = useNotifier();
  const [pwMode, setPwMode] = useState<PasswordMode>("auto");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [touched, setTouched] = useState(false);

  // Reset internal state whenever the dialog transitions closed → open
  // OR the parent flips us into the RESULT phase. The password fields
  // must never persist across opens.
  useEffect(() => {
    if (open) {
      setPwMode("auto");
      setPw("");
      setPw2("");
      setShowPw(false);
      setTouched(false);
    }
  }, [open]);

  const inResult = !!credential;

  const pwError = useMemo(() => {
    if (pwMode !== "manual") return null;
    const trimmed = pw.trim();
    if (!trimmed) return null;
    return validateManualPassword(trimmed);
  }, [pwMode, pw]);
  const mismatchError = pwMode === "manual" && pw && pw2 && pw !== pw2 ? "Passwords do not match." : null;

  const canSubmit = useMemo(() => {
    if (isSubmitting) return false;
    if (pwMode === "auto") return true;
    if (!pw || !pw2) return false;
    if (pwError || mismatchError) return false;
    return true;
  }, [pwMode, pw, pw2, pwError, mismatchError, isSubmitting]);

  const doSubmit = () => {
    setTouched(true);
    if (pwMode === "auto") {
      onSubmit({ mode: "auto", password: null });
      return;
    }
    const err = validateManualPassword(pw.trim()) || (pw !== pw2 ? "Passwords do not match." : null);
    if (err) return;
    onSubmit({ mode: "manual", password: pw });
  };

  const copy = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard?.writeText(text)
      .then(() => showMessage(`${label} copied`))
      .catch(() => {});
  };

  const title = inResult
    ? "Staff Portal Account Created"
    : mode === "create"
    ? "Create Staff Portal Account"
    : "Reset Staff Portal Password";

  const submitLabel = mode === "create" ? "Create Account" : "Reset Password";

  const companyName = useSelector((s: any) => s?.company?.companyName as string | undefined) ?? "";
  const portalHeading = companyName ? `${companyName} Staff Portal` : "Staff Portal";
  const loginDetails = `${portalHeading}\n\nLogin Email: ${loginEmail || "—"}\nTemporary Password: ${credential?.password || ""}`;

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 18 }}>{title}</DialogTitle>
      <DialogContent dividers>
        {/* Identity block — always visible; read-only. */}
        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 0.5, rowGap: 0.75, mb: 2, fontSize: 13 }}>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Staff</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{staffName || "—"}</Typography>
          {staffIdLabel && (
            <>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Staff ID</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{staffIdLabel}</Typography>
            </>
          )}
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Login Email</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, wordBreak: "break-all" }}>{loginEmail || "—"}</Typography>
          {mode === "reset" && passwordIsSet !== undefined && (
            <>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Current Password</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: "50%",
                  bgcolor: passwordIsSet ? "#16a34a" : "#94a3b8",
                }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {passwordIsSet ? "Set" : "Not Set"}
                </Typography>
              </Box>
            </>
          )}
        </Box>
        {mode === "reset" && (
          <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontStyle: "italic", mt: -1, mb: 2 }}>
            For security, existing passwords cannot be displayed. Set a new temporary password below.
          </Typography>
        )}

        {!inResult && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Password Setup</Typography>
            <RadioGroup
              value={pwMode}
              onChange={(_, v) => {
                // Clear all sensitive form state on every mode switch.
                // Manual → Auto: an admin-entered password never lingers
                // in memory. Auto → Manual: the manual fields open EMPTY
                // (the reported bug — no lingering value, no browser-
                // autofill trigger from a stale render).
                setPwMode(v as PasswordMode);
                setPw("");
                setPw2("");
                setShowPw(false);
                setTouched(false);
              }}
            >
              <FormControlLabel
                value="auto"
                control={<Radio size="small" />}
                label={<Typography sx={{ fontSize: 13 }}>Auto-generate secure password (recommended)</Typography>}
              />
              <FormControlLabel
                value="manual"
                control={<Radio size="small" />}
                label={<Typography sx={{ fontSize: 13 }}>Choose password manually</Typography>}
              />
            </RadioGroup>

            {pwMode === "manual" && (
              <Box sx={{ mt: 1.5 }}>
                {/* Hidden dummy fields defeat Chrome/Edge password-
                    manager autofill. Chrome will look for the first
                    visible input[type=password] on the form and
                    inject the admin's OWN saved credential — which
                    is what pre-populated the field before this fix.
                    Two throwaway inputs above the real fields absorb
                    that heuristic. `aria-hidden` + `tabIndex={-1}`
                    keep them off screen readers and tab order.
                    autoComplete="new-password" tells compliant
                    browsers this is a NEW password field, not a
                    login. Unique field names help browsers that
                    still fingerprint by name. */}
                <input
                  type="text"
                  name="prevent-autofill-username"
                  autoComplete="username"
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }}
                />
                <input
                  type="password"
                  name="prevent-autofill-password"
                  autoComplete="new-password"
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type={showPw ? "text" : "password"}
                  label="New password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  onBlur={() => setTouched(true)}
                  error={touched && !!pwError}
                  helperText={(touched && pwError) || "At least 8 characters, must include a letter and a number."}
                  name="staff_portal_new_password"
                  autoComplete="new-password"
                  inputProps={{
                    autoComplete: "new-password",
                    "data-lpignore": "true",     // LastPass
                    "data-1p-ignore": "true",    // 1Password
                    "data-form-type": "other",   // generic form-fill hint
                  }}
                  sx={{ mb: 1.25 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPw((v) => !v)} tabIndex={-1}>
                          {showPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type={showPw ? "text" : "password"}
                  label="Confirm password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  onBlur={() => setTouched(true)}
                  error={touched && !!mismatchError}
                  helperText={touched && mismatchError ? mismatchError : " "}
                  name="staff_portal_confirm_password"
                  autoComplete="new-password"
                  inputProps={{
                    autoComplete: "new-password",
                    "data-lpignore": "true",
                    "data-1p-ignore": "true",
                    "data-form-type": "other",
                  }}
                />
              </Box>
            )}
          </>
        )}

        {inResult && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fefce8", border: "1px solid #fde68a", mb: 2 }}>
              <Typography sx={{ fontSize: 12.5, color: "#854d0e", fontWeight: 600 }}>
                Save or send these credentials now. For security, the password will not be shown again.
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>Temporary password</Typography>
            <Box sx={{
              px: 1.5, py: 1.25, borderRadius: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              border: "1px solid rgba(0,0,0,0.08)", fontFamily: "monospace", fontSize: 15, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
              wordBreak: "break-all",
            }}>
              <span>{credential.password}</span>
              <IconButton size="small" onClick={() => copy(credential.password, "Password")}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
              <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={() => copy(loginEmail || "", "Email")}>Copy Email</Button>
              <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={() => copy(credential.password, "Password")}>Copy Password</Button>
              <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={() => copy(loginDetails, "Login details")}>Copy Login Details</Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {inResult ? (
          <Button variant="contained" onClick={onClose} sx={{ textTransform: "none", borderRadius: 2 }}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={isSubmitting} sx={{ textTransform: "none", borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={doSubmit}
              disabled={!canSubmit}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}
            >
              {isSubmitting ? <CircularProgress size={16} color="inherit" /> : submitLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StaffPortalCredentialDialog;
